import type { Filter } from "../filters/model";

/** Sort direction for one column. */
export interface Sort {
  column: string;
  direction: "asc" | "desc";
}

/**
 * What every list screen asks for.
 *
 * Every list takes the same query shape on purpose: the filter builder, saved views, column
 * chooser and pagination are built once in F3 and inherited. A module that
 * needs its own query shape has almost certainly reinvented one of those.
 */
export interface ListQuery {
  /** The active filter tree. */
  filter?: Filter;
  sort?: Sort[];
  /** Free-text search across the module's searchable fields. */
  search?: string;
  /**
   * Orders only (#621): keep the orders whose number is one of these exactly,
   * as `orderNumberKey` writes them. A paste of order numbers, not a search:
   * "143" finds order 143 and not 1430.
   */
  orderNumbers?: string[];
  /**
   * Rows per page, up to the route's maximum in `LIST_LIMITS`. The API refuses
   * more with a 400 rather than quietly sending fewer; the fixture adapter
   * caps it.
   */
  limit?: number;
  /**
   * Opaque continuation token from the previous page.
   *
   * A cursor rather than an offset because offsets skip and repeat rows when
   * the underlying data changes between pages, and orders arrive while
   * someone is paging through them.
   */
  cursor?: string;
}

/**
 * How many rows a list route sends when a request names no `limit`, and the
 * most it will send when one does — a request above `max` is a 400.
 *
 * Here, not in each route, because both sides read it: the API refuses by it,
 * and the SPA asks for exactly it where it wants everything a route will give
 * in one request (every task, every project, a folder's pages). Four call sites
 * sent their route's maximum as a bare number, so raising a constant on one
 * side would have turned them into 400s.
 *
 * Keyed by the route group that owns the list.
 */
export const LIST_LIMITS = {
  orders: { fallback: 25, max: 250 },
  catalog: { fallback: 25, max: 250 },
  inventory: { fallback: 25, max: 250 },
  warehouses: { fallback: 25, max: 250 },
  contacts: { fallback: 25, max: 250 },
  customers: { fallback: 25, max: 250 },
  settings: { fallback: 25, max: 250 },
  tasks: { fallback: 25, max: 200 },
  docs: { fallback: 25, max: 200 },
  sync: { fallback: 25, max: 100 },
  notifications: { fallback: 20, max: 100 },
} as const;

/** One page of results. */
export interface Page<T> {
  items: T[];
  /** Null when this is the last page. */
  nextCursor: string | null;
  /**
   * Total matching rows, where the adapter can count cheaply. Null when it
   * cannot — better an absent count than a wrong one.
   */
  total: number | null;
}

/**
 * Why the channel or the server refused a write.
 *
 * These are expected outcomes, not bugs. Each one is something a user can
 * see, understand and act on.
 */
/**
 * One field that differs between what a form loaded and what the channel
 * holds now — "show what changed" on a conflict (spec §6.2). Values are as a
 * person reads them (tags comma-joined), `null` for empty.
 */
export interface ConflictChange {
  field: string;
  loaded: string | null;
  current: string | null;
}

export type WriteRejection =
  /**
   * Someone changed the record at the channel after this form was loaded.
   * `changes` lists what differs, where it was cheap to tell.
   */
  | {
      kind: "conflict";
      message: string;
      currentUpdatedAt: string;
      changes?: ConflictChange[];
    }
  /** The channel refused. `message` is its own words, shown verbatim. */
  | { kind: "channel_rejected"; message: string }
  /** The input is invalid. `fields` maps field name to what is wrong with it. */
  | { kind: "invalid"; message: string; fields?: Record<string, string> }
  /** The caller lacks the permission this action requires. */
  | { kind: "forbidden"; message: string; permission: string }
  /** A precondition failed — an invoice with no HSN code, a transfer with no lines. */
  | { kind: "blocked"; message: string; details?: unknown };

/**
 * The result of a write.
 *
 * A union rather than a thrown error, because these rejections are expected
 * and the screen has to render them. Making them a return value means a
 * caller cannot forget to handle one. Transport failures — the server is
 * down, the network dropped — still throw, because there is nothing
 * meaningful for a screen to say about those beyond "try again".
 *
 * `ok: true` carries the record as the channel returned it, never as the form
 * submitted it. A write is not applied locally until the channel accepts it,
 * and the local row is written from the response.
 */
export type WriteResult<T> =
  { ok: true; data: T } | { ok: false; rejection: WriteRejection };

/**
 * An edit that must not silently overwrite someone else's.
 *
 * The caller sends the `updatedAt` it observed when the form was loaded. If
 * the record has moved on, the write is refused with a `conflict` and the
 * user is shown what changed. Last-write-wins is not acceptable for fields a
 * second person may be editing in Shopify admin at the same moment.
 */
export interface Guarded {
  expectedUpdatedAt: string;
}
